import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import PersonIcon from '@material-ui/icons/Person';
import RestaurantIcon from '@material-ui/icons/Restaurant';
import DescriptionIcon from '@material-ui/icons/Description';
import NoteIcon from '@material-ui/icons/Note';
import HistoryIcon from '@material-ui/icons/History';
import moment from 'moment';
import { request } from 'utilities/graph';
import { getElderServiceEvent } from 'graphql/queries';
import { elderServiceEventOptions } from '@silvergatedelivery/constants';
import elderDeactivationReason from '@silvergatedelivery/constants/src/elderDeactivationReason';

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: 700,
  },
  dialogTitle: {
    position: 'relative',
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: theme.palette.primary.contrastText,
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  eventHeader: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    textAlign: 'center',
  },
  eventTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  eventSubtitle: {
    opacity: 0.9,
    fontSize: '1rem',
  },
  eventDetails: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    'fontSize': '1.1rem',
    'fontWeight': 600,
    'marginBottom': theme.spacing(2),
    'color': theme.palette.text.primary,
    'display': 'flex',
    'alignItems': 'center',
    '& svg': {
      marginRight: theme.spacing(1),
      fontSize: '1.2rem',
    },
  },
  detailCard: {
    borderRadius: theme.spacing(2),
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    border: 'none',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
  },
  detailLabel: {
    'fontWeight': 600,
    'color': theme.palette.text.secondary,
    'minWidth': 100,
    'display': 'flex',
    'alignItems': 'center',
    '& svg': {
      marginRight: theme.spacing(1),
      fontSize: '1rem',
    },
  },
  detailValue: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  dateValue: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
    flexDirection: 'column',
  },
  loadingText: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  description: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.info[50],
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.info[200]}`,
  },
  note: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.warning[50],
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.warning[200]}`,
  },
  mealCard: {
    borderRadius: theme.spacing(2),
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    border: 'none',
    background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
  },
  mealRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  mealLabel: {
    fontWeight: 600,
    fontSize: '1rem',
    marginRight: theme.spacing(5),
  },
  recordCard: {
    borderRadius: theme.spacing(2),
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    border: 'none',
    background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  chip: {
    fontWeight: 600,
    fontSize: '0.875rem',
  },
}));

export default function ElderServiceEventDialog({
  open,
  onClose,
  id,
  onEdit,
  onDelete,
}) {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !id) return;

    const loadEvent = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: { getElderServiceEvent: event } } = await request(getElderServiceEvent, { id });
        setEvent(event);
      } catch (err) {
        console.error('載入失敗:', err);
        setError('載入失敗');
      } finally {
        setIsLoading(false);
      }
    };

    loadEvent();
  }, [open, id]);

  const getEventOption = (type) => {
    return elderServiceEventOptions.find((option) => option.id === type) || {
      label: '未知類型',
      color: '#999',
      icon: null,
    };
  };

  const handleEdit = () => {
    if (event && onEdit) {
      onEdit(event);
    }
  };

  const handleDelete = () => {
    if (event && onDelete) {
      onDelete(event);
    }
  };

  if (!open) return null;

  if (!event) return null;

  const dateString = event.endedAt ? (
    `${moment(event.startedAt).format('YYYY年MM月DD日')} - ${moment(event.endedAt).subtract(1, 'day').format('YYYY年MM月DD日')}`
  ) : (
    moment(event.startedAt).format('YYYY年MM月DD日')
  );

  let reason = event.description;
  if (event.type === 'serviceStop') {
    const deactivationReason = elderDeactivationReason.find((reason) => reason.value === event.description);
    reason = deactivationReason ? deactivationReason.label : event.description;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">
          服務管理詳情
        </Typography>
        <IconButton
          onClick={onClose}
          className={classes.closeButton}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {isLoading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={40} />
            <Typography className={classes.loadingText}>
              載入中...
            </Typography>
          </div>
        ) : error ? (
          <Alert severity="error" style={{ borderRadius: 12 }}>
            {error}
          </Alert>
        ) : (
          <>
            <div
              className={classes.eventHeader}
              style={{
                background: `linear-gradient(135deg, ${getEventOption(event.type).color}20 0%, ${getEventOption(event.type).color}40 100%)`,
                color: getEventOption(event.type).color,
                border: `2px solid ${getEventOption(event.type).color}30`,
              }}
            >
              <Typography className={classes.eventTitle}>
                {getEventOption(event.type).label}
              </Typography>
              <Typography className={classes.eventSubtitle}>
                {event.endedAt ? (<>
                  {dateString}
                  <span style={{ marginLeft: 8, fontWeight: 600 }}>
                    ({moment(event.endedAt).diff(moment(event.startedAt), 'days')}天)
                  </span></>) : (
                  <>{dateString}</>
                )}
              </Typography>
            </div>

            {(event.type === 'leave' || event.type === 'medical') && (
              <div className={classes.eventDetails}>
                <Typography className={classes.sectionTitle}>
                  <RestaurantIcon />
                  取消餐期
                </Typography>
                <Card className={classes.mealCard}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <div className={classes.mealRow}>
                          <Typography className={classes.mealLabel}>
                            午餐
                          </Typography>
                          <Chip label={event.lunchCancelled ? '取消' : '未取消'} className={classes.chip} />
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className={classes.mealRow}>
                          <Typography className={classes.mealLabel}>
                            晚餐
                          </Typography>
                          <Chip label={event.dinnerCancelled ? '取消' : '未取消'} className={classes.chip} />
                        </div>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </div>
            )}

            <Divider className={classes.divider} />

            {reason && (
              <div className={classes.description}>
                <Typography className={classes.sectionTitle}>
                  <DescriptionIcon />
                  原因
                </Typography>
                <Typography variant="body1">
                  {reason}
                </Typography>
              </div>
            )}

            {event.note && (
              <div className={classes.note}>
                <Typography className={classes.sectionTitle}>
                  <NoteIcon />
                  備註
                </Typography>
                <Typography variant="body1">
                  {event.note}
                </Typography>
              </div>
            )}

            <Divider className={classes.divider} />

            <div className={classes.eventDetails}>
              <Typography className={classes.sectionTitle}>
                <HistoryIcon />
                紀錄
              </Typography>
              <Card className={classes.recordCard}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <div className={classes.detailRow}>
                        <Typography className={classes.detailLabel}>
                          <PersonIcon />
                          創立者
                        </Typography>
                        <Typography className={classes.detailValue}>
                          {event.createdBy || '系統'}
                        </Typography>
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <div className={classes.detailRow}>
                        <Typography className={classes.detailLabel}>
                          <AccessTimeIcon />
                          創立於
                        </Typography>
                        <Typography className={classes.detailValue}>
                          {moment(event.createdAt).format('YYYY-MM-DD HH:mm')}
                        </Typography>
                      </div>
                    </Grid>
                    {event.updatedAt && event.updatedAt !== event.createdAt && (
                      <Grid item xs={12} sm={6}>
                        <div className={classes.detailRow}>
                          <Typography className={classes.detailLabel}>
                            <PersonIcon />
                            更新者
                          </Typography>
                          <Typography className={classes.detailValue}>
                            {event.updatedBy}
                          </Typography>
                        </div>
                      </Grid>
                    )}
                    {event.updatedAt && event.updatedAt !== event.createdAt && (
                      <Grid item xs={12} sm={6}>
                        <div className={classes.detailRow}>
                          <Typography className={classes.detailLabel}>
                            <AccessTimeIcon />
                            更新於
                          </Typography>
                          <Typography className={classes.detailValue}>
                            {moment(event.updatedAt).format('YYYY-MM-DD HH:mm')}
                          </Typography>
                        </div>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>
        <Button
          onClick={handleDelete}
          variant="outlined"
          color="secondary"
          startIcon={<DeleteIcon />}
          style={{ borderRadius: 8, marginRight: 'auto' }}
        >
          刪除
        </Button>
        <Button
          onClick={handleEdit}
          variant="outlined"
          color="primary"
          startIcon={<EditIcon />}
          style={{ borderRadius: 8, marginRight: 8 }}
        >
          修改
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          style={{ borderRadius: 8 }}
        >
          關閉
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ElderServiceEventDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.string,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};
